import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const statusStyle = {
  Good:              { background: '#ECFDF5', color: '#065F46' },
  'Needs Attention': { background: '#FEF3C7', color: '#92400E' },
  Critical:          { background: '#FDECEA', color: '#B91C1C' },
};

const today = new Date().toLocaleDateString('en-US', {
  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
});

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function startOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString();
}

function startOfDay() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { session } = useAuth();

  const [statsLoading, setStatsLoading] = useState(true);
  const [clientsLoading, setClientsLoading] = useState(true);

  const [totalClients, setTotalClients] = useState(0);
  const [reportsThisMonth, setReportsThisMonth] = useState(0);
  const [criticalCount, setCriticalCount] = useState(0);
  const [needsAttentionCount, setNeedsAttentionCount] = useState(0);
  const [reportsTodayCount, setReportsTodayCount] = useState(0);
  const [recentClients, setRecentClients] = useState([]);

  useEffect(() => {
    if (!session?.user?.id) return;
    const uid = session.user.id;

    async function fetchStats() {
      setStatsLoading(true);
      const [clientsRes, reportsRes, criticalRes, needsAttentionRes, reportsTodayRes] = await Promise.all([
        supabase.from('clients').select('*', { count: 'exact', head: true }).eq('user_id', uid),
        supabase.from('reports').select('*', { count: 'exact', head: true }).eq('user_id', uid).gte('created_at', startOfMonth()),
        supabase.from('clients').select('*', { count: 'exact', head: true }).eq('user_id', uid).eq('status', 'Critical'),
        supabase.from('clients').select('*', { count: 'exact', head: true }).eq('user_id', uid).eq('status', 'Needs Attention'),
        supabase.from('reports').select('*', { count: 'exact', head: true }).eq('user_id', uid).gte('created_at', startOfDay()),
      ]);

      if (clientsRes.error) console.error('clients count error:', clientsRes.error);
      else setTotalClients(clientsRes.count ?? 0);

      if (reportsRes.error) console.error('reports count error:', reportsRes.error);
      else setReportsThisMonth(reportsRes.count ?? 0);

      if (criticalRes.error) console.error('critical count error:', criticalRes.error);
      else setCriticalCount(criticalRes.count ?? 0);

      if (needsAttentionRes.error) console.error('needs attention count error:', needsAttentionRes.error);
      else setNeedsAttentionCount(needsAttentionRes.count ?? 0);

      if (reportsTodayRes.error) console.error('reports today error:', reportsTodayRes.error);
      else setReportsTodayCount(reportsTodayRes.count ?? 0);

      setStatsLoading(false);
    }

    async function fetchRecentClients() {
      setClientsLoading(true);
      const { data, error } = await supabase
        .from('clients')
        .select('id, business_name, domain, industry, status, created_at')
        .eq('user_id', uid)
        .order('created_at', { ascending: false })
        .limit(5);
      if (error) console.error('recent clients error:', error);
      else setRecentClients(data || []);
      setClientsLoading(false);
    }

    fetchStats();
    fetchRecentClients();
  }, [session]);

  const stats = [
    {
      label: 'Total Clients',
      value: statsLoading ? '—' : String(totalClients),
      detail: totalClients === 1 ? '1 client total' : `${totalClients} clients total`,
    },
    {
      label: 'Reports Run This Month',
      value: statsLoading ? '—' : String(reportsThisMonth),
      detail: 'Across all clients',
    },
    {
      label: 'Keywords Tracked',
      value: '0',
      detail: 'Connect DataForSEO to track',
    },
    {
      label: 'AI Citations Found',
      value: '0',
      detail: 'Run an AI Visibility Audit',
      gold: true,
    },
  ];

  const thisWeek = [
    { label: 'Critical clients',   value: statsLoading ? '—' : String(criticalCount),        color: '#B91C1C'       },
    { label: 'Needs attention',    value: statsLoading ? '—' : String(needsAttentionCount),   color: '#92400E'       },
    { label: 'Reports run today',  value: statsLoading ? '—' : String(reportsTodayCount),     color: 'var(--gold)'  },
  ];

  return (
    <div>

      {/* Header */}
      <div style={{ marginBottom: '2.25rem' }}>
        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.75rem',
          fontWeight: 400,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'var(--charcoal-muted)',
          marginBottom: '0.4rem',
        }}>
          {today}
        </p>
        <h1 style={{
          fontFamily: 'var(--font-heading)',
          fontSize: '2.4rem',
          fontWeight: 600,
          color: 'var(--charcoal)',
          lineHeight: 1.15,
        }}>
          SEO & GEO Intelligence Hub
        </h1>
        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.9rem',
          color: 'var(--charcoal-muted)',
          marginTop: '0.4rem',
        }}>
          Your weekly performance snapshot across all clients.
        </p>
      </div>

      {/* Stats Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '1.25rem',
        marginBottom: '2.25rem',
      }}>
        {stats.map((stat, i) => (
          <div key={i} style={{
            background: 'var(--white)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            padding: '1.4rem 1.5rem',
            boxShadow: 'var(--shadow-sm)',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0,
              height: '3px',
              background: stat.gold ? 'var(--gold)' : 'var(--blush)',
            }} />
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.7rem',
              fontWeight: 500,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--charcoal-muted)',
              marginBottom: '0.6rem',
            }}>
              {stat.label}
            </p>
            <div style={{
              fontFamily: 'var(--font-heading)',
              fontSize: '2.1rem',
              fontWeight: 600,
              color: statsLoading && i < 2 ? 'var(--charcoal-muted)' : 'var(--charcoal)',
              lineHeight: 1,
              marginBottom: '0.4rem',
            }}>
              {stat.value}
            </div>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.75rem',
              color: 'var(--charcoal-muted)',
            }}>
              {stat.detail}
            </p>
          </div>
        ))}
      </div>

      {/* Lower section */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 260px',
        gap: '1.25rem',
        alignItems: 'start',
      }}>

        {/* Client Activity Table */}
        <div style={{
          background: 'var(--white)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-sm)',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '1.25rem 1.5rem',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <h3 style={{
              fontFamily: 'var(--font-heading)',
              fontSize: '1.2rem',
              fontWeight: 600,
              color: 'var(--charcoal)',
            }}>
              Recent Client Activity
            </h3>
            <button
              onClick={() => navigate('/clients')}
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.75rem',
                fontWeight: 500,
                color: 'var(--blush-dark)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                letterSpacing: '0.03em',
              }}
            >
              View all →
            </button>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--off-white)' }}>
                {['Client', 'Domain', 'Industry', 'Added', 'Status'].map(col => (
                  <th key={col} style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    letterSpacing: '0.09em',
                    textTransform: 'uppercase',
                    color: 'var(--charcoal-muted)',
                    padding: '0.65rem 1rem',
                    textAlign: 'left',
                    borderBottom: '1px solid var(--border)',
                  }}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clientsLoading ? (
                <tr>
                  <td colSpan={5} style={{
                    padding: '2.5rem',
                    textAlign: 'center',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    color: 'var(--charcoal-muted)',
                  }}>
                    Loading…
                  </td>
                </tr>
              ) : recentClients.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{
                    padding: '2.5rem',
                    textAlign: 'center',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    color: 'var(--charcoal-muted)',
                  }}>
                    No clients yet — add your first client to get started.
                  </td>
                </tr>
              ) : recentClients.map((client, i) => (
                <tr
                  key={client.id}
                  onClick={() => navigate('/clients')}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--off-white)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                >
                  <td style={{
                    padding: '0.9rem 1rem',
                    borderBottom: i < recentClients.length - 1 ? '1px solid var(--border)' : 'none',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    color: 'var(--charcoal)',
                  }}>
                    {client.business_name}
                  </td>
                  <td style={{
                    padding: '0.9rem 1rem',
                    borderBottom: i < recentClients.length - 1 ? '1px solid var(--border)' : 'none',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8rem',
                    color: 'var(--charcoal-muted)',
                  }}>
                    {client.domain}
                  </td>
                  <td style={{
                    padding: '0.9rem 1rem',
                    borderBottom: i < recentClients.length - 1 ? '1px solid var(--border)' : 'none',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8rem',
                    color: 'var(--charcoal-muted)',
                  }}>
                    {client.industry || '—'}
                  </td>
                  <td style={{
                    padding: '0.9rem 1rem',
                    borderBottom: i < recentClients.length - 1 ? '1px solid var(--border)' : 'none',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8rem',
                    color: 'var(--charcoal-muted)',
                    whiteSpace: 'nowrap',
                  }}>
                    {formatDate(client.created_at)}
                  </td>
                  <td style={{
                    padding: '0.9rem 1rem',
                    borderBottom: i < recentClients.length - 1 ? '1px solid var(--border)' : 'none',
                  }}>
                    <span style={{
                      ...(statusStyle[client.status] ?? statusStyle.Good),
                      display: 'inline-flex',
                      alignItems: 'center',
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.72rem',
                      fontWeight: 500,
                      letterSpacing: '0.04em',
                      padding: '0.2rem 0.7rem',
                      borderRadius: '999px',
                      whiteSpace: 'nowrap',
                    }}>
                      {client.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Quick Actions + This Week */}
        <div style={{
          background: 'var(--white)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-sm)',
          padding: '1.4rem 1.5rem',
        }}>
          <h3 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '1.2rem',
            fontWeight: 600,
            color: 'var(--charcoal)',
            marginBottom: '1.25rem',
          }}>
            Quick Actions
          </h3>

          <button
            onClick={() => navigate('/clients')}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--blush-dark)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--blush)'}
            style={{
              width: '100%',
              background: 'var(--blush)',
              color: 'var(--charcoal)',
              fontFamily: 'var(--font-body)',
              fontWeight: 500,
              fontSize: '0.875rem',
              letterSpacing: '0.03em',
              padding: '0.75rem 1rem',
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              cursor: 'pointer',
              marginBottom: '0.75rem',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: '0.6rem',
              transition: 'background 0.2s ease',
            }}
          >
            <span>＋</span> Add New Client
          </button>

          <button
            onClick={() => navigate('/run')}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--charcoal-light)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--charcoal)'}
            style={{
              width: '100%',
              background: 'var(--charcoal)',
              color: 'var(--white)',
              fontFamily: 'var(--font-body)',
              fontWeight: 500,
              fontSize: '0.875rem',
              letterSpacing: '0.03em',
              padding: '0.75rem 1rem',
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: '0.6rem',
              transition: 'background 0.2s ease',
            }}
          >
            <span>▷</span> Run Report
          </button>

          {/* This Week */}
          <div style={{
            marginTop: '1.5rem',
            paddingTop: '1.25rem',
            borderTop: '1px solid var(--border)',
          }}>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.7rem',
              fontWeight: 500,
              letterSpacing: '0.09em',
              textTransform: 'uppercase',
              color: 'var(--charcoal-muted)',
              marginBottom: '0.85rem',
            }}>
              This Week
            </p>
            {thisWeek.map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.55rem',
              }}>
                <span style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.8rem',
                  color: 'var(--charcoal-light)',
                }}>
                  {item.label}
                </span>
                <span style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1.05rem',
                  fontWeight: 600,
                  color: item.value === '0' ? 'var(--charcoal-muted)' : item.color,
                }}>
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
